import { defineStore } from 'pinia';
import type { GameStatistics, ServerStatistics } from '~/api/services/daemon/types';
import { ServerStatus } from '~/api/services/daemon/types';

export interface SocketStore {
    connected: boolean;
    error?: string;
    status?: ServerStatus;
    proc?: ServerStatistics;
    query?: GameStatistics;
}

export const useSocketStore = defineStore('socket', {
    state: (): SocketStore => ({
        connected: false,
        error: undefined,
        status: undefined,
        proc: undefined,
        query: undefined,
    }),

    getters: {
        isConnected: (state) => state.connected,
        hasError: (state) => !!state.error,
        connectionError: (state) => state.error,

        // Server status getters
        serverStatus: (state) => state.status,
        isServerOnline: (state) => state.status === ServerStatus.ON,
        isServerOffline: (state) => state.status === ServerStatus.OFF || state.status === undefined,
        isServerStarting: (state) => state.status === ServerStatus.STARTING,
        isServerStopping: (state) => state.status === ServerStatus.STOPPING,

        // Server statistics getters
        serverStats: (state) => state.proc,
        cpuUsage: (state) => state.proc?.cpuUsed,
        memoryUsage: (state) => state.proc?.memoryUsed,
        diskUsage: (state) => state.proc?.diskUsed,
        networkStats: (state) => state.proc?.network,
        networkRx: (state) => state.proc?.network?.rxBytes,
        networkTx: (state) => state.proc?.network?.txBytes,

        // Game statistics getters
        gameStats: (state) => state.query,
        gameName: (state) => state.query?.name,
        gameDescription: (state) => state.query?.description,
        gameMode: (state) => state.query?.gamemode,
        gameVersion: (state) => state.query?.version,
        gameMap: (state) => state.query?.map,
        hasPassword: (state) => state.query?.password || false,
        maxPlayers: (state) => state.query?.maxplayers,
        currentPlayers: (state) => state.query?.numplayers,
        playerList: (state) => state.query?.players || [],
        botList: (state) => state.query?.bots || [],
        playerCount: (state) => (state.query?.players || []).length,
        botCount: (state) => (state.query?.bots || []).length,
        totalPlayers: (state) => (state.query?.numplayers || 0) + (state.query?.bots || []).length,

        // Computed status getters
        connectionStatus: (state) => {
            if (state.error) return 'error';
            if (state.connected) return 'connected';
            return 'disconnected';
        },

        serverStatusText: (state) => {
            switch (state.status) {
                case ServerStatus.ON:
                    return 'online';
                case ServerStatus.OFF:
                    return 'offline';
                case ServerStatus.STARTING:
                    return 'starting';
                case ServerStatus.STOPPING:
                    return 'stopping';
                default:
                    return 'unknown';
            }
        },

        // Resource usage helpers
        hasResourceStats: (state) => !!state.proc,
        resourceUsage: (state) => {
            if (!state.proc) return null;
            return {
                cpu: state.proc.cpuUsed,
                memory: state.proc.memoryUsed,
                disk: state.proc.diskUsed,
                network: state.proc.network,
            };
        },

        // Game info helpers
        hasGameStats: (state) => !!state.query,
        gameInfo: (state) => {
            if (!state.query) return null;
            return {
                name: state.query.name,
                description: state.query.description,
                gamemode: state.query.gamemode,
                version: state.query.version,
                map: state.query.map,
                hasPassword: state.query.password,
                players: {
                    current: state.query.numplayers,
                    max: state.query.maxplayers,
                    list: state.query.players,
                    bots: state.query.bots,
                },
            };
        },
    },

    actions: {
        setState(connected: boolean) {
            if (connected) {
                this.error = undefined;
            }
            this.connected = connected;
        },

        setError(error?: string) {
            this.error = error;
        },

        setStatus(status?: ServerStatus) {
            this.status = status;
        },

        setProc(proc?: ServerStatistics) {
            this.proc = proc;
        },

        setQuery(query?: GameStatistics) {
            this.query = query;
        },

        // Enhanced actions for better usability
        connect() {
            this.setState(true);
        },

        disconnect(error?: string) {
            this.setState(false);
            if (error) {
                this.setError(error);
            }
        },

        clearError() {
            this.error = undefined;
        },

        updateServerStatus(status: ServerStatus) {
            this.setStatus(status);
        },

        updateServerStats(stats: ServerStatistics) {
            this.setProc(stats);
        },

        updateGameStats(stats: GameStatistics) {
            this.setQuery(stats);
        },

        // Batch update for multiple data types
        updateServerData(data: { status?: ServerStatus; proc?: ServerStatistics; query?: GameStatistics }) {
            if (data.status !== undefined) this.setStatus(data.status);
            if (data.proc !== undefined) this.setProc(data.proc);
            if (data.query !== undefined) this.setQuery(data.query);
        },

        // Reset all server data
        resetServerData() {
            this.status = undefined;
            this.proc = undefined;
            this.query = undefined;
        },

        // Reset everything including connection
        reset() {
            this.connected = false;
            this.error = undefined;
            this.status = undefined;
            this.proc = undefined;
            this.query = undefined;
        },
    },
});
